"use client";

import type {
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  PointerEvent as ReactPointerEvent,
} from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  animate,
  useMotionValue,
  useSpring,
  type MotionValue,
} from "framer-motion";
import {
  NEEDLE_ENTRANCE_DELAY_MS,
  NEEDLE_FADE_IN_MS,
  NEEDLE_FADE_OUT_MS,
  NEEDLE_SNAP_SPRING,
} from "./timeline-constants";
import { findNearestTimelineSnapCandidate } from "./timeline-root-selectors";
import type { ParsedTimeline } from "./timeline-types";
import type {
  SnapCandidate,
  TimelineLayoutMetrics,
} from "./timeline-root-types";

export interface UseTimelineNeedleControllerInput {
  clearTimelineTooltip: () => void;
  getInnerXFromClient: (clientX: number) => number | null;
  getViewportCenterInInner: () => number | null;
  layout: TimelineLayoutMetrics | null;
  parsed: ParsedTimeline | null;
  snapCandidates: SnapCandidate[];
  viewportScrollLeft: number;
}

export interface TimelineNeedleController {
  handleNeedleMouseEnter: (event: ReactMouseEvent<HTMLDivElement>) => void;
  handleNeedleMouseLeave: () => void;
  handleNeedleMouseMove: (event: ReactMouseEvent<HTMLDivElement>) => void;
  handleTouchFocus: (event: ReactPointerEvent<HTMLDivElement>) => void;
  isNeedleReady: boolean;
  needleActive: boolean;
  needleOpacity: MotionValue<number>;
  resetTimelineNeedle: () => void;
  snappedNeedleX: MotionValue<number>;
  snappedPeriodIndex: number | null;
}

interface TimelineNeedleHoverCapabilityInput {
  needleOpacity: MotionValue<number>;
  setNeedleActive: (needleActive: boolean) => void;
}

interface TimelineNeedleSnapControllerInput {
  canUseHover: boolean;
  getInnerXFromClient: (clientX: number) => number | null;
  getViewportCenterInInner: () => number | null;
  layout: TimelineLayoutMetrics | null;
  mouseClientXRef: MutableRefObject<number>;
  needleActive: boolean;
  needleX: MotionValue<number>;
  parsed: ParsedTimeline | null;
  snapCandidates: SnapCandidate[];
  viewportScrollLeft: number;
}

interface TimelineNeedleSnapStateInput {
  needleX: MotionValue<number>;
  snapCandidates: SnapCandidate[];
}

interface TimelineNeedleSnapController {
  resetSnappedPeriodIndex: () => void;
  snapNeedleToClosest: (contentX: number) => void;
  snappedPeriodIndex: number | null;
}

interface TimelineNeedleSnapValidityInput {
  getViewportCenterInInner: () => number | null;
  resetSnappedPeriodIndex: () => void;
  snapCandidates: SnapCandidate[];
  snapNeedleToClosest: (contentX: number) => void;
  snappedPeriodIndex: number | null;
}

interface TimelineNeedleEntranceSnapInput {
  getViewportCenterInInner: () => number | null;
  layout: TimelineLayoutMetrics | null;
  parsed: ParsedTimeline | null;
  snapCandidates: SnapCandidate[];
  snapNeedleToClosest: (contentX: number) => void;
}

interface TimelineNeedleScrollSnapInput {
  canUseHover: boolean;
  getInnerXFromClient: (clientX: number) => number | null;
  mouseClientXRef: MutableRefObject<number>;
  needleActive: boolean;
  snapNeedleToClosest: (contentX: number) => void;
  viewportScrollLeft: number;
}

interface TimelineNeedleHandlerInput {
  canUseHover: boolean;
  clearTimelineTooltip: () => void;
  getInnerXFromClient: (clientX: number) => number | null;
  isNeedleReady: boolean;
  mouseClientXRef: MutableRefObject<number>;
  needleActive: boolean;
  needleOpacity: MotionValue<number>;
  setNeedleActive: (needleActive: boolean) => void;
  snapNeedleToClosest: (contentX: number) => void;
}

interface TimelineNeedleHandlers {
  handleNeedleMouseEnter: (event: ReactMouseEvent<HTMLDivElement>) => void;
  handleNeedleMouseLeave: () => void;
  handleNeedleMouseMove: (event: ReactMouseEvent<HTMLDivElement>) => void;
  handleTouchFocus: (event: ReactPointerEvent<HTMLDivElement>) => void;
}

interface TimelineNeedleHoverGuardInput {
  canUseHover: boolean;
  isNeedleReady: boolean;
}

interface TimelineNeedleTouchGuardInput extends TimelineNeedleHoverGuardInput {
  pointerType: string;
}

function shouldHandleHoverNeedle({
  canUseHover,
  isNeedleReady,
}: TimelineNeedleHoverGuardInput): boolean {
  return canUseHover && isNeedleReady;
}

function shouldHandleTouchNeedle({
  canUseHover,
  isNeedleReady,
  pointerType,
}: TimelineNeedleTouchGuardInput): boolean {
  return !canUseHover && isNeedleReady && pointerType !== "mouse";
}

function useTimelineNeedleHoverCapability({
  needleOpacity,
  setNeedleActive,
}: TimelineNeedleHoverCapabilityInput): boolean {
  const [canUseHover, setCanUseHover] = useState<boolean>(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.matchMedia("(hover: hover)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(hover: hover)");

    const handleMediaChange = (): void => {
      setCanUseHover(mediaQuery.matches);
      if (!mediaQuery.matches) {
        setNeedleActive(false);
        needleOpacity.set(0);
      }
    };

    mediaQuery.addEventListener("change", handleMediaChange);

    return () => {
      mediaQuery.removeEventListener("change", handleMediaChange);
    };
  }, [needleOpacity, setNeedleActive]);

  return canUseHover;
}

function useTimelineNeedleSnapState({
  needleX,
  snapCandidates,
}: TimelineNeedleSnapStateInput): TimelineNeedleSnapController {
  const [snappedPeriodIndex, setSnappedPeriodIndex] = useState<number | null>(
    null,
  );

  const resetSnappedPeriodIndex = useCallback((): void => {
    setSnappedPeriodIndex(null);
  }, []);

  const snapNeedleToClosest = useCallback(
    (contentX: number): void => {
      const nearestCandidate = findNearestTimelineSnapCandidate({
        candidates: snapCandidates,
        x: contentX,
      });

      if (!nearestCandidate) {
        return;
      }

      needleX.set(nearestCandidate.centerX);
      setSnappedPeriodIndex((previous) => {
        return previous === nearestCandidate.index
          ? previous
          : nearestCandidate.index;
      });
    },
    [needleX, snapCandidates],
  );

  return {
    resetSnappedPeriodIndex,
    snapNeedleToClosest,
    snappedPeriodIndex,
  };
}

function useTimelineNeedleSnapValidity({
  getViewportCenterInInner,
  resetSnappedPeriodIndex,
  snapCandidates,
  snapNeedleToClosest,
  snappedPeriodIndex,
}: TimelineNeedleSnapValidityInput): void {
  useEffect(() => {
    if (snapCandidates.length === 0) {
      const clearFrame = window.requestAnimationFrame(() => {
        resetSnappedPeriodIndex();
      });

      return () => {
        window.cancelAnimationFrame(clearFrame);
      };
    }

    const currentStillValid =
      snappedPeriodIndex != null &&
      snapCandidates.some(
        (candidate) => candidate.index === snappedPeriodIndex,
      );

    if (currentStillValid) {
      return;
    }

    const snapFrame = window.requestAnimationFrame(() => {
      const centerInInner = getViewportCenterInInner();
      snapNeedleToClosest(centerInInner ?? snapCandidates[0].centerX);
    });

    return () => {
      window.cancelAnimationFrame(snapFrame);
    };
  }, [
    getViewportCenterInInner,
    resetSnappedPeriodIndex,
    snapCandidates,
    snappedPeriodIndex,
    snapNeedleToClosest,
  ]);
}

function useTimelineNeedleEntranceSnap({
  getViewportCenterInInner,
  layout,
  parsed,
  snapCandidates,
  snapNeedleToClosest,
}: TimelineNeedleEntranceSnapInput): void {
  useEffect(() => {
    if (!parsed || !layout || snapCandidates.length === 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      const centerInInner = getViewportCenterInInner();
      snapNeedleToClosest(centerInInner ?? snapCandidates[0].centerX);
    }, NEEDLE_ENTRANCE_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    getViewportCenterInInner,
    layout,
    parsed,
    snapCandidates,
    snapNeedleToClosest,
  ]);
}

function useTimelineNeedleScrollSnap({
  canUseHover,
  getInnerXFromClient,
  mouseClientXRef,
  needleActive,
  snapNeedleToClosest,
  viewportScrollLeft,
}: TimelineNeedleScrollSnapInput): void {
  useEffect(() => {
    if (!needleActive || !canUseHover || mouseClientXRef.current < 0) {
      return;
    }

    const resnapFrame = window.requestAnimationFrame(() => {
      const contentX = getInnerXFromClient(mouseClientXRef.current);
      if (contentX == null) {
        return;
      }
      snapNeedleToClosest(contentX);
    });

    return () => {
      window.cancelAnimationFrame(resnapFrame);
    };
  }, [
    canUseHover,
    getInnerXFromClient,
    mouseClientXRef,
    needleActive,
    snapNeedleToClosest,
    viewportScrollLeft,
  ]);
}

function useTimelineNeedleSnapController(
  input: TimelineNeedleSnapControllerInput,
): TimelineNeedleSnapController {
  const snapState = useTimelineNeedleSnapState({
    needleX: input.needleX,
    snapCandidates: input.snapCandidates,
  });

  useTimelineNeedleSnapValidity({
    getViewportCenterInInner: input.getViewportCenterInInner,
    resetSnappedPeriodIndex: snapState.resetSnappedPeriodIndex,
    snapCandidates: input.snapCandidates,
    snapNeedleToClosest: snapState.snapNeedleToClosest,
    snappedPeriodIndex: snapState.snappedPeriodIndex,
  });
  useTimelineNeedleEntranceSnap({
    getViewportCenterInInner: input.getViewportCenterInInner,
    layout: input.layout,
    parsed: input.parsed,
    snapCandidates: input.snapCandidates,
    snapNeedleToClosest: snapState.snapNeedleToClosest,
  });
  useTimelineNeedleScrollSnap({
    canUseHover: input.canUseHover,
    getInnerXFromClient: input.getInnerXFromClient,
    mouseClientXRef: input.mouseClientXRef,
    needleActive: input.needleActive,
    snapNeedleToClosest: snapState.snapNeedleToClosest,
    viewportScrollLeft: input.viewportScrollLeft,
  });

  return snapState;
}

function useNeedleReadyTimer({
  layout,
  parsed,
  snapCandidates,
}: {
  layout: TimelineLayoutMetrics | null;
  parsed: ParsedTimeline | null;
  snapCandidates: SnapCandidate[];
}): [boolean, (isNeedleReady: boolean) => void] {
  const [isNeedleReady, setIsNeedleReady] = useState<boolean>(false);

  useEffect(() => {
    if (!parsed || !layout || snapCandidates.length === 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      setIsNeedleReady(true);
    }, NEEDLE_ENTRANCE_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [layout, parsed, snapCandidates]);

  return [isNeedleReady, setIsNeedleReady];
}

function useNeedleMouseEnterHandler({
  canUseHover,
  getInnerXFromClient,
  isNeedleReady,
  mouseClientXRef,
  needleOpacity,
  setNeedleActive,
  snapNeedleToClosest,
}: TimelineNeedleHandlerInput): (event: ReactMouseEvent<HTMLDivElement>) => void {
  return useCallback(
    (event: ReactMouseEvent<HTMLDivElement>): void => {
      if (!shouldHandleHoverNeedle({ canUseHover, isNeedleReady })) {
        return;
      }

      const contentX = getInnerXFromClient(event.clientX);
      if (contentX == null) {
        return;
      }

      mouseClientXRef.current = event.clientX;
      setNeedleActive(true);
      snapNeedleToClosest(contentX);

      animate(needleOpacity, 1, {
        duration: NEEDLE_FADE_IN_MS / 1000,
        ease: "easeOut",
      });
    },
    [
      canUseHover,
      getInnerXFromClient,
      isNeedleReady,
      mouseClientXRef,
      needleOpacity,
      setNeedleActive,
      snapNeedleToClosest,
    ],
  );
}

function useNeedleMouseMoveHandler({
  canUseHover,
  getInnerXFromClient,
  isNeedleReady,
  mouseClientXRef,
  needleActive,
  needleOpacity,
  setNeedleActive,
  snapNeedleToClosest,
}: TimelineNeedleHandlerInput): (event: ReactMouseEvent<HTMLDivElement>) => void {
  return useCallback(
    (event: ReactMouseEvent<HTMLDivElement>): void => {
      if (!shouldHandleHoverNeedle({ canUseHover, isNeedleReady })) {
        return;
      }

      const contentX = getInnerXFromClient(event.clientX);
      if (contentX == null) {
        return;
      }

      mouseClientXRef.current = event.clientX;

      if (!needleActive) {
        setNeedleActive(true);
        animate(needleOpacity, 1, {
          duration: NEEDLE_FADE_IN_MS / 1000,
          ease: "easeOut",
        });
      }

      snapNeedleToClosest(contentX);
    },
    [
      canUseHover,
      getInnerXFromClient,
      isNeedleReady,
      mouseClientXRef,
      needleActive,
      needleOpacity,
      setNeedleActive,
      snapNeedleToClosest,
    ],
  );
}

function useNeedleMouseLeaveHandler({
  canUseHover,
  clearTimelineTooltip,
  isNeedleReady,
  mouseClientXRef,
  needleOpacity,
  setNeedleActive,
}: TimelineNeedleHandlerInput): () => void {
  return useCallback((): void => {
    if (!shouldHandleHoverNeedle({ canUseHover, isNeedleReady })) {
      return;
    }

    setNeedleActive(false);
    mouseClientXRef.current = -1;
    clearTimelineTooltip();

    animate(needleOpacity, 0, {
      duration: NEEDLE_FADE_OUT_MS / 1000,
      ease: "easeOut",
    });
  }, [
    canUseHover,
    clearTimelineTooltip,
    isNeedleReady,
    mouseClientXRef,
    needleOpacity,
    setNeedleActive,
  ]);
}

function useNeedleTouchFocusHandler({
  canUseHover,
  clearTimelineTooltip,
  getInnerXFromClient,
  isNeedleReady,
  needleOpacity,
  setNeedleActive,
  snapNeedleToClosest,
}: TimelineNeedleHandlerInput): (
  event: ReactPointerEvent<HTMLDivElement>,
) => void {
  return useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      if (
        !shouldHandleTouchNeedle({
          canUseHover,
          isNeedleReady,
          pointerType: event.pointerType,
        })
      ) {
        return;
      }

      const contentX = getInnerXFromClient(event.clientX);
      if (contentX == null) {
        return;
      }

      setNeedleActive(true);
      clearTimelineTooltip();
      needleOpacity.set(1);
      snapNeedleToClosest(contentX);
    },
    [
      canUseHover,
      clearTimelineTooltip,
      getInnerXFromClient,
      isNeedleReady,
      needleOpacity,
      setNeedleActive,
      snapNeedleToClosest,
    ],
  );
}

function useTimelineNeedleHandlers(
  input: TimelineNeedleHandlerInput,
): TimelineNeedleHandlers {
  const handleNeedleMouseEnter = useNeedleMouseEnterHandler(input);
  const handleNeedleMouseLeave = useNeedleMouseLeaveHandler(input);
  const handleNeedleMouseMove = useNeedleMouseMoveHandler(input);
  const handleTouchFocus = useNeedleTouchFocusHandler(input);

  return {
    handleNeedleMouseEnter,
    handleNeedleMouseLeave,
    handleNeedleMouseMove,
    handleTouchFocus,
  };
}

export function useTimelineNeedleController({
  clearTimelineTooltip,
  getInnerXFromClient,
  getViewportCenterInInner,
  layout,
  parsed,
  snapCandidates,
  viewportScrollLeft,
}: UseTimelineNeedleControllerInput): TimelineNeedleController {
  const [needleActive, setNeedleActive] = useState<boolean>(false);
  const mouseClientXRef = useRef<number>(-1);
  const needleX = useMotionValue(0);
  const snappedNeedleX = useSpring(needleX, NEEDLE_SNAP_SPRING);
  const needleOpacity = useMotionValue(0);
  const canUseHover = useTimelineNeedleHoverCapability({
    needleOpacity,
    setNeedleActive,
  });
  const [isNeedleReady, setIsNeedleReady] = useNeedleReadyTimer({
    layout,
    parsed,
    snapCandidates,
  });
  const {
    resetSnappedPeriodIndex,
    snapNeedleToClosest,
    snappedPeriodIndex,
  } = useTimelineNeedleSnapController({
    canUseHover,
    getInnerXFromClient,
    getViewportCenterInInner,
    layout,
    mouseClientXRef,
    needleActive,
    needleX,
    parsed,
    snapCandidates,
    viewportScrollLeft,
  });
  const handlerInput: TimelineNeedleHandlerInput = {
    canUseHover,
    clearTimelineTooltip,
    getInnerXFromClient,
    isNeedleReady,
    mouseClientXRef,
    needleActive,
    needleOpacity,
    setNeedleActive,
    snapNeedleToClosest,
  };
  const handlers = useTimelineNeedleHandlers(handlerInput);

  const resetTimelineNeedle = useCallback((): void => {
    resetSnappedPeriodIndex();
    setNeedleActive(false);
    setIsNeedleReady(false);
    mouseClientXRef.current = -1;
    needleOpacity.set(0);
  }, [needleOpacity, resetSnappedPeriodIndex, setIsNeedleReady]);

  return {
    ...handlers,
    isNeedleReady,
    needleActive,
    needleOpacity,
    resetTimelineNeedle,
    snappedNeedleX,
    snappedPeriodIndex,
  };
}
